import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { COMPANY, LEGAL_NOTICE, policiesFor, type LegalAudience } from '@shared/utils/legal.content';

/**
 * The compliance notice + the required policies for one audience, as one block.
 *
 * Shared because three surfaces show exactly this: the public /legal page (both audiences,
 * behind a toggle), the vendor profile's Legal modal ('vendor') and the customer profile's
 * Legal modal ('customer'). Policies open one at a time so the block reads as a short list.
 * Copy lives in `legal.customer.ts` / `legal.vendor.ts`.
 */
@Component({
  selector: 'app-legal-notice',
  standalone: true,
  styleUrl: './legal-notice.component.scss',
  imports: [CommonModule],
  templateUrl: './legal-notice.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalNoticeComponent {
  /** Which policy pack to show. Collapses everything when it changes. */
  @Input() set audience(v: LegalAudience) { this.audienceSig.set(v); this.openKey.set(null); }

  private readonly audienceSig = signal<LegalAudience>('customer');

  readonly notice = LEGAL_NOTICE;
  readonly company = COMPANY;
  readonly policies = computed(() => policiesFor(this.audienceSig()));

  /** Which policy is expanded — one at a time, none on arrival. */
  readonly openKey = signal<string | null>(null);

  toggle(key: string): void {
    this.openKey.set(this.openKey() === key ? null : key);
  }
}
